/**
 * emailTypo — "Did you mean …@gmail.com?" (2026-09-17).
 *
 * Owner report: a player signed up as `…@gmail.con`, never got a single email
 * and was locked out when she forgot her password. Pure helper shared by the
 * web sign-up form and the profile "Change email" card; mobile mirror at
 * mobile/src/lib/emailTypo.js. Returns a corrected full address or null —
 * never blocks, the user always decides.
 */
const KNOWN_DOMAINS = [
  'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.co.uk', 'yahoo.ca', 'ymail.com',
  'hotmail.com', 'hotmail.co.uk', 'hotmail.ca', 'outlook.com', 'live.com', 'live.ca', 'msn.com',
  'icloud.com', 'me.com', 'mac.com', 'aol.com', 'protonmail.com', 'proton.me', 'gmx.com',
  'mail.com', 'zoho.com', 'comcast.net', 'att.net', 'verizon.net', 'sbcglobal.net', 'cox.net',
];

// 2026-11-01 — real regional/provider domains that sit 1–2 edits from a
// KNOWN_DOMAINS entry (yahoo.fr ≈ yahoo.ca, protonmail.ch ≈ protonmail.com):
// never "correct" these, and never suggest them either.
const VALID_ONLY = new Set([
  'protonmail.ch', 'pm.me', 'yahoo.fr', 'yahoo.de', 'yahoo.es', 'yahoo.it', 'yahoo.in', 'yahoo.co.in',
  'yahoo.com.au', 'yahoo.com.br', 'yahoo.co.jp', 'hotmail.fr', 'hotmail.de', 'hotmail.es', 'hotmail.it',
  'outlook.fr', 'outlook.de', 'outlook.es', 'outlook.it', 'live.co.uk', 'live.fr', 'live.de', 'live.it',
  'gmx.de', 'gmx.net', 'gmx.at', 'gmx.ch', 'web.de', 'mail.ru', 'yandex.ru', 'yandex.com', 'qq.com',
  '163.com', 'naver.com', 'orange.fr', 'free.fr', 'wanadoo.fr', 'libero.it', 't-online.de', 'aol.co.uk',
  'rocketmail.com', 'btinternet.com', 'att.com',
]);

const TLD_FIXES = {
  con: 'com', cm: 'com', co: 'com', om: 'com', comm: 'com', cpm: 'com', vom: 'com', xom: 'com',
  ocm: 'com', cmo: 'com', cim: 'com', clm: 'com', cok: 'com', ent: 'net', nte: 'net', nett: 'net',
  ogr: 'org', orgg: 'org', c: 'com',
};

// Damerau–Levenshtein (optimal string alignment) — transposition costs 1 so
// "gmial" is one edit from "gmail".
function editDistance(a, b) {
  const d = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = 0; i <= a.length; i += 1) d[i][0] = i;
  for (let j = 0; j <= b.length; j += 1) d[0][j] = j;
  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[a.length][b.length];
}

function closestKnown(domain) {
  let best = null;
  let bestDist = Infinity;
  for (const known of KNOWN_DOMAINS) {
    const dist = editDistance(domain, known);
    if (dist < bestDist) { best = known; bestDist = dist; }
  }
  // Allow 1 edit for short names, 2 for longer ones (gmial/gmaill/iclould),
  // but never rewrite most of the domain.
  const maxDist = domain.length >= 8 ? 2 : 1;
  return best && bestDist > 0 && bestDist <= maxDist ? best : null;
}

export function suggestEmailFix(raw) {
  const typed = (raw || '').trim().toLowerCase();
  const at = typed.lastIndexOf('@');
  if (at <= 0 || at === typed.length - 1) return null;
  const local = typed.slice(0, at).trimEnd();
  if (!local || /\s/.test(local)) return null;
  // A stray space after "@" from autocorrect ("bob@gmail .com") — drop it.
  const domain = typed.slice(at + 1).replace(/\s+/g, '');
  const email = `${local}@${domain}`;
  if (KNOWN_DOMAINS.includes(domain) || VALID_ONLY.has(domain)) return email !== typed ? email : null;

  let fixed = null;
  if (!domain.includes('.')) {
    // "gmailcom" / "gmail" — the dot or the TLD went missing.
    fixed = KNOWN_DOMAINS.find((k) => k.replace(/\./g, '') === domain || k.split('.')[0] === domain) || null;
    if (!fixed) {
      const m = domain.match(/^(.+?)(com|net|org)$/);
      if (m) fixed = closestKnown(`${m[1]}.${m[2]}`) || (KNOWN_DOMAINS.includes(`${m[1]}.${m[2]}`) ? `${m[1]}.${m[2]}` : null);
    }
  } else {
    const parts = domain.split('.');
    const tld = parts[parts.length - 1];
    if (TLD_FIXES[tld]) {
      const candidate = [...parts.slice(0, -1), TLD_FIXES[tld]].join('.');
      fixed = KNOWN_DOMAINS.includes(candidate) ? candidate : closestKnown(candidate);
    }
    if (!fixed) fixed = closestKnown(domain);
  }
  if (fixed && fixed !== domain) return `${local}@${fixed}`;
  return email !== typed ? email : null;
}

export default suggestEmailFix;
